import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusSmIcon } from "@heroicons/react/outline";
import { ChevronRightIcon } from "@heroicons/react/solid";
import { BiPrinter } from "react-icons/bi";
import CustomButton from "components/common/Button/CustomButton";
import NotificationDropDown from "components/common/NotificationDropDown/NotificationDropDown";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import {
  baseURLImage,
  formatDateIntoDMYToDisplay,
  getFormSubTabForParticipantProfile,
  getFormSubTabForSilProfile,
  getNameProfile,
  selectedTabState,
  SilHomeselectedTabState,
} from "Utils/utils";
import { PARTICIPANT_PROFILE_TABS, USER_ROLE, getParticipantLabel } from "Constants/constant";
import { roleAccess } from "helpers/RolesPermission";
import { useSelector } from "react-redux";
import { getSpSetting } from "store/selector/sp.selector";
import FormScopeBadge from "./FormScopeBadge";
import { resolveFormScopeFromRole } from "./formCategoryUtils";

const saveParticipantFormsTab = (parentSlug) => {
  const subTab = getFormSubTabForParticipantProfile(parentSlug);
  if (subTab) {
    selectedTabState.save(JSON.stringify(subTab));
    return;
  }
  const formsTab = PARTICIPANT_PROFILE_TABS.find((tab) => tab.id === 4);
  if (formsTab) {
    selectedTabState.save(JSON.stringify(formsTab));
  }
};

export const buildParticipantFormBreadcrumbPages = ({
  formData,
  participantUuid,
  participantName,
  formTitle,
  parentSlug,
  routeRole = null,
  role = null,
  careSector = null,
}) => {
  const pages = [];
  const uuid =
    participantUuid ||
    formData?.participant_uuid ||
    formData?.worker_uuid ||
    null;
  const name =
    participantName ||
    formData?.submitted_against ||
    formData?.worker_name ||
    "Profile";
  const resolvedRole = routeRole || role;

  if (resolvedRole === "sil") {
    pages.push({ name: "SIL Homes", href: "/forms-accommodation" });
    if (uuid) {
      pages.push({
        name,
        href: `/sil-accommodation/${uuid}`,
        onBeforeNavigate: () => {
          const tab = getFormSubTabForSilProfile(parentSlug);
          if (tab) SilHomeselectedTabState.save(tab);
        },
      });
    }
  } else if (resolvedRole === "worker") {
    pages.push({ name: "Submitted Forms", href: "/submitted-forms" });
    if (uuid && formData?.participant_uuid) {
      pages.push({
        name,
        href: `/participant-profile/${uuid}`,
        onBeforeNavigate: () => saveParticipantFormsTab(parentSlug),
      });
    }
  } else if (resolvedRole === "hrm") {
    pages.push({ name: "Team Management", href: "/hrm/team-management" });
    if (uuid) {
      pages.push({
        name,
        href: `/${
          roleAccess([USER_ROLE.Sp_Standard_User])
            ? "participant-profile"
            : "worker-profile"
        }/${uuid}`,
        onBeforeNavigate: () => saveParticipantFormsTab(parentSlug),
      });
    }
  } else if (uuid) {
    const participantLabelPlural = getParticipantLabel(careSector, true);
    pages.push({
      name: participantLabelPlural,
      href: roleAccess([USER_ROLE.Sp_Standard_User])
        ? "/participants"
        : "/participant-list",
    });
    pages.push({
      name,
      href: `/${
        roleAccess([USER_ROLE.Sp_Standard_User])
          ? "participant-profile"
          : "sp-profile"
      }/${uuid}`,
      onBeforeNavigate: () => saveParticipantFormsTab(parentSlug),
    });
  }

  if (formTitle && pages.length > 0) {
    pages.push({ name: formTitle, href: "#", isCurrent: true });
  }

  return pages;
};

export const buildFormTemplateReadOnlyBreadcrumbPages = ({
  formTitle,
  role = "crm",
}) => {
  const formsListHref =
    role === "sil"
      ? "/forms-accommodation"
      : role === "hrm"
      ? "/forms-list-hrm"
      : "/forms-list";

  const pages = [{ name: "Forms", href: formsListHref }];

  if (formTitle) {
    pages.push({ name: formTitle, href: "#", isCurrent: true });
  }

  return pages;
};

export const FormPageBreadcrumb = ({
  pages: pagesOverride = null,
  formData,
  participantUuid,
  participantName,
  formTitle,
  parentSlug,
  routeRole = null,
  role = null,
}) => {
  const navigate = useNavigate();
  const spSettings = useSelector(getSpSetting);
  const careSector = spSettings?.care_sector ?? null;

  const pages = useMemo(() => {
    if (pagesOverride?.length) {
      return pagesOverride;
    }

    return buildParticipantFormBreadcrumbPages({
      formData,
      participantUuid,
      participantName,
      formTitle,
      parentSlug,
      routeRole,
      role,
      careSector,
    });
  }, [
    pagesOverride,
    formData,
    participantUuid,
    participantName,
    formTitle,
    parentSlug,
    routeRole,
    role,
    careSector,
  ]);

  if (!pages.length) return null;

  return (
    <nav className="form-page-breadcrumb" aria-label="Breadcrumb">
      <ol className="form-page-breadcrumb-list">
        {pages.map((page, idx) => (
          <li key={`${page.name}-${idx}`} className="form-page-breadcrumb-item">
            {idx > 0 && (
              <ChevronRightIcon
                className="form-page-breadcrumb-sep"
                aria-hidden="true"
              />
            )}
            {page.isCurrent ? (
              <span className="form-page-breadcrumb-current" aria-current="page">
                {page.name}
              </span>
            ) : (
              <button
                type="button"
                className="form-page-breadcrumb-link"
                onClick={() => {
                  page.onBeforeNavigate?.();
                  if (page.href && page.href !== "#") {
                    navigate(page.href);
                  }
                }}
              >
                {page.name}
              </button>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export const useFormPageHeaderHeight = (pageRef, headerRef, deps = []) => {
  useEffect(() => {
    const header = headerRef?.current;
    const page = pageRef?.current;
    if (!header || !page) return undefined;

    const syncHeaderHeight = () => {
      page.style.setProperty(
        "--form-page-header-height",
        `${header.offsetHeight}px`
      );
    };

    syncHeaderHeight();
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(header);
    window.addEventListener("resize", syncHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncHeaderHeight);
    };
  }, deps);
};

export const FormPageHeader = React.forwardRef(function FormPageHeader(
  {
    title,
    subtitle,
    sectionCount,
    status,
    breadcrumb,
    onBack,
    onPrint,
    editLink,
    extraActions,
    showNotifications = true,
    role = null,
    scope = null,
    showScopeBadge = true,
  },
  ref
) {
  const scopeKey =
    scope ?? (role != null ? resolveFormScopeFromRole(role) : null);

  return (
  <div ref={ref} className="form-page-header print:hidden">
    <div className="min-w-0">
      {breadcrumb}
      <div className="form-page-title-row">
        <h1 className="form-page-title capitalize truncate">{title}</h1>
        {showScopeBadge && scopeKey ? (
          <FormScopeBadge scope={scopeKey} />
        ) : null}
      </div>
      {(sectionCount || status || subtitle) && (
        <div className="form-page-subtitle-row">
          {status && (
            <span
              className={`form-page-status-badge ${
                status === "complete" ? "is-complete" : "is-in-progress"
              }`}
            >
              {status === "complete" ? "Completed" : "In progress"}
            </span>
          )}
          {sectionCount > 0 && (
            <span className="form-page-meta">
              {sectionCount} section{sectionCount !== 1 ? "s" : ""}
            </span>
          )}
          {!sectionCount && !status && subtitle && (
            <span className="form-page-meta">{subtitle}</span>
          )}
        </div>
      )}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {editLink && (
        <Link
          to={editLink.to}
          state={editLink.state}
          className="form-page-primary-action"
        >
          <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
          {editLink.label}
        </Link>
      )}
      {extraActions}
      {onPrint && (
        <CustomButton
          clickHandler={onPrint}
          label="Print"
          Icon={<BiPrinter />}
          variant="secondary"
          cssClass="form-btn-outline"
        />
      )}
      {onBack && <CustomButton clickHandler={onBack} label="Back" />}
      {showNotifications && <NotificationDropDown />}
    </div>
  </div>
  );
});

FormPageHeader.displayName = "FormPageHeader";

export const FormMetaStrip = ({ items = [], className = "" }) => (
  <div className={`form-meta-strip ${className}`.trim()}>
    {items.map((item, idx) => (
      <div key={idx} className="form-meta-item">
        {item.avatarSrc && (
          <img
            onError={item.onAvatarError}
            className="form-meta-avatar"
            src={item.avatarSrc}
            alt=""
          />
        )}
        <div className="form-meta-content">
          <p className="form-meta-label">{item.label}</p>
          <p
            className={`form-meta-value ${item.capitalize ? "capitalize" : ""} ${
              !item.value ? "is-empty" : ""
            }`}
          >
            {item.value ?? "—"}
          </p>
        </div>
      </div>
    ))}
  </div>
);

export const buildSubmittedFormMetaItems = ({
  formData,
  routeRole,
  handleImageError,
}) => {
  const items = [];

  if (routeRole !== "sil") {
    items.push({
      label: "Submitted against",
      value: formData?.submitted_against,
      capitalize: true,
      avatarSrc: formData?.participant_pic
        ? baseURLImage(formData.participant_pic)
        : getNameProfile(formData?.submitted_against || " ", " "),
      onAvatarError: (e) =>
        handleImageError?.(e, formData?.submitted_against || " ", " "),
    });
  }

  items.push(
    {
      label: "Submitted by",
      value: formData?.submitted_by,
      capitalize: true,
      avatarSrc: formData?.profile_picture
        ? baseURLImage(formData.profile_picture)
        : getNameProfile(formData?.submitted_by || " ", " "),
      onAvatarError: (e) =>
        handleImageError?.(e, formData?.submitted_by || " ", " "),
    },
    {
      label: "Submitted on",
      value: formatDateIntoDMYToDisplay(formData?.posted_by),
    },
    {
      label: "Last updated by",
      value: formData?.updated_by,
      capitalize: true,
    },
    {
      label: "Last updated",
      value: formatDateIntoDMYToDisplay(formData?.updated_at),
    }
  );

  return items;
};

const truncateNavLabel = (title, max = 24) => {
  if (!title) return "Section";
  return title.length > max ? `${title.slice(0, max)}…` : title;
};

/** Short label when several sections share a long title, e.g. Terms and Conditions. */
export const compactDuplicateLabel = (title, part) => {
  const normalized = String(title || "Section").trim();
  const lower = normalized.toLowerCase();

  if (lower.includes("terms") && lower.includes("condition")) {
    return `T&C ${part}`;
  }

  if (normalized.length > 20) {
    const words = normalized.split(/\s+/).filter(Boolean);
    if (words.length > 2) {
      return `${words.slice(0, 2).join(" ")} ${part}`;
    }
  }

  return `${normalized} ${part}`;
};

export const buildSectionNavLabels = (sections = []) => {
  const titleCounts = {};
  sections.forEach((section) => {
    const key = section?.section_title || "";
    titleCounts[key] = (titleCounts[key] || 0) + 1;
  });

  const titleSeen = {};
  return sections.map((section) => {
    const title = section?.section_title || "Section";
    if (titleCounts[title] > 1) {
      titleSeen[title] = (titleSeen[title] || 0) + 1;
      return compactDuplicateLabel(title, titleSeen[title]);
    }
    return truncateNavLabel(title, 26);
  });
};

export const FormSectionNav = ({
  sections = [],
  sectionIdPrefix = "form-section",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navLabels = useMemo(() => buildSectionNavLabels(sections), [sections]);

  useEffect(() => {
    if (sections.length < 2) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          const idx = parseInt(
            visible[0].target.id.replace(`${sectionIdPrefix}-`, ""),
            10
          );
          if (!Number.isNaN(idx)) setActiveIndex(idx);
        }
      },
      { rootMargin: "-18% 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((_, idx) => {
      const el = document.getElementById(`${sectionIdPrefix}-${idx}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections, sectionIdPrefix]);

  if (sections.length < 2) return null;

  const scrollToSection = (idx) => {
    setActiveIndex(idx);
    document
      .getElementById(`${sectionIdPrefix}-${idx}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav className="form-section-nav print:hidden" aria-label="Jump to section">
      <div className="form-section-nav-inner custom-scroll">
        {sections.map((section, idx) => (
          <button
            key={`${section?.section_title}-${idx}`}
            type="button"
            className={`form-section-nav-item${
              activeIndex === idx ? " is-active" : ""
            }${idx < activeIndex ? " is-complete" : ""}`}
            onClick={() => scrollToSection(idx)}
            title={section?.section_title}
          >
            <span className="form-section-nav-num">{idx + 1}</span>
            <span className="form-section-nav-label">{navLabels[idx]}</span>
          </button>
        ))}
      </div>
    </nav>
  );
};

export const FormSectionList = ({
  sections = [],
  formData,
  renderFields,
  className = "",
  sectionIdPrefix = "form-section",
  fillMode = false,
}) => (
  <div className={`form-section-list ${className}`.trim()}>
    {sections.map((section, index) => (
      <article
        id={`${sectionIdPrefix}-${index}`}
        key={`${section?.section_title}-${index}`}
        className={`form-section-card${fillMode ? " form-section-card--preview" : ""}`}
      >
        <header className="form-section-header">
          <div className="form-section-header-main">
            <span className="form-section-index" aria-hidden="true">
              {index + 1}
            </span>
            <div>
              <h2 className="form-section-title capitalize">
                {section?.section_title}
              </h2>
              <p className="form-section-meta">
                Section {index + 1} of {sections.length}
              </p>
            </div>
          </div>
        </header>
        <div className="form-section-body">
          <div
            className={`form-field-grid${fillMode ? "" : " form-view-mode"}`.trim()}
          >
            {renderFields(section, index, formData)}
          </div>
        </div>
      </article>
    ))}
  </div>
);

export const FormPageLoader = () => (
  <div className="form-page text-center min-h-screen flex justify-center items-center">
    <CircularLoader classes="flex justify-center items-center" classloader="loader-l" />
  </div>
);

export const FormViewPage = ({ children, sideBarPadding, header, bodyClassName = "" }) => {
  const pageRef = useRef(null);
  const headerRef = useRef(null);

  useFormPageHeaderHeight(pageRef, headerRef, []);

  return (
    <FormPageShell sideBarPadding={sideBarPadding}>
      <div ref={pageRef} className="form-view-page">
        {header && React.cloneElement(header, { ref: headerRef })}
        <div className={`form-view-body form-edit-layout ${bodyClassName}`.trim()}>
          {children}
        </div>
      </div>
    </FormPageShell>
  );
};

export const FormPageShell = ({ children, sideBarPadding }) => (
  <div
    className={`duration-300 form-page min-h-screen flex flex-col ${
      sideBarPadding ? "lg:ml-[4rem]" : "lg:ml-64"
    }`}
  >
    {children}
  </div>
);
